import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Gantt, Task, ViewMode } from 'gantt-task-react';
import { useTranslation } from 'react-i18next';
import "gantt-task-react/dist/index.css";
import styles from './PhasePlan.module.scss';
import { ProjectDto, PhaseDto, SprintDto, ReleaseDto, MilestoneDto, DeliverObjectDto, DeliverObjectDependencyDto } from '../../api/model';
import { IconTimelineEventPlus, IconArrowsSplit2, IconLink } from '@tabler/icons-react';
import { useProjectPhases } from '../../hooks/useProjectPhases';
import { useProjectDependencies, useDeleteDependency, useUpdateDependency } from '../../hooks/useDeliverableDependencies';
import { useProjectReleases } from '../../hooks/useProjectReleases';
import { useProjectDeliverables } from '../../hooks/useProjectDeliverables';
import { getSprint, putSprintId } from '../../api/sprint-api/sprint-api';
import { getMilestoneIdPhase, putMilestoneId } from '../../api/milestone-api/milestone-api';
import { putPhaseId } from '../../api/phase-api/phase-api';
import { putReleaseId } from '../../api/release-api/release-api';
import { putDeliverObjectId } from '../../api/deliver-object-api/deliver-object-api';
import { useGetIntelligenceProjectProjectIdTaskCompletion } from '../../api/intelligence-api/intelligence-api';
import {getPhaseDisplayName, isAgileArtefactDeliverable} from '../../utils/phaseUtils';
import { useToast } from '../ui/Toast/ToastProvider';
import { getApiErrorMessage } from '../../hooks/useApiMutation';
import ConfirmationDialog from '../ui/ConfirmationDialog/ConfirmationDialog';
import AddNewItemPopup from './AddNewItemPopup';
import GanttItemDetailModal from './GanttItemDetailModal';
import DeliverableDetailPopup from '../Deliverables/components/DeliverableDetailPopup';
import DependencyModal from '../Dependencies/DependencyModal';

interface PhasePlanProps {
  project?: ProjectDto;
  onProjectUpdate?: () => void;
}

const PhasePlan: React.FC<PhasePlanProps> = ({ project, onProjectUpdate }) => {
  const { i18n, t } = useTranslation('gantt');
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.Month);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [chartHeight, setChartHeight] = useState<number>(window.innerHeight * 0.65);
  const [columnWidth, setColumnWidth] = useState<number>(150);
  //for future usable states
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [selectedTaskData, setSelectedTaskData] = useState<PhaseDto | SprintDto | ReleaseDto | MilestoneDto | null>(null);
  const [selectedTaskType, setSelectedTaskType] = useState<'phase' | 'sprint' | 'release' | 'milestone' | 'deliverable' | null>(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [isDeliverableDetailOpen, setIsDeliverableDetailOpen] = useState(false);
  const [isDependencyModalOpen, setIsDependencyModalOpen] = useState(false);
  const [dependencyDefaults, setDependencyDefaults] = useState<{ predecessorId?: number; successorId?: number }>({});
  const [clickedDependency, setClickedDependency] = useState<DeliverObjectDependencyDto | null>(null);
  const [isDependencyEditModalOpen, setIsDependencyEditModalOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showUpdateConfirm, setShowUpdateConfirm] = useState(false);
  const [pendingUpdateData, setPendingUpdateData] = useState<DeliverObjectDependencyDto | null>(null);
  const [showAgileArtefacts, setShowAgileArtefacts] = useState(false);
  // Link mode: click two deliverable bars on the chart to connect them.
  const [linkMode, setLinkMode] = useState(false);
  const [linkSourceId, setLinkSourceId] = useState<number | null>(null);
  const [selectedDeliverable, setSelectedDeliverable] = useState<DeliverObjectDto | null>(null);
  const [isAddNewItemPopupOpen, setIsAddNewItemPopupOpen] = useState(false);
  
  // State for date change confirmation
  interface PendingDateChange {
    task: Task;
    originalTask: Task;
    oldStartDate: string;
    oldEndDate: string;
    newStartDate: string;
    newEndDate: string;
    oldStartDateDisplay: string;
    oldEndDateDisplay: string;
    newStartDateDisplay: string;
    newEndDateDisplay: string;
    itemType: 'phase' | 'sprint' | 'release' | 'deliverable' | 'milestone';
    itemName: string;
  }
  const [pendingDateChange, setPendingDateChange] = useState<PendingDateChange | null>(null);
  const [showDateChangeDialog, setShowDateChangeDialog] = useState(false);

  // expand state for showing deliverables under phases/sprints/releases
  const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(new Set());
  const toggleExpand = useCallback((id: string) => {
    setExpandedRowIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  
  const toast = useToast();
  const queryClient = useQueryClient();
  const isDraggingRef = useRef(false);
  const deleteDependencyMutation = useDeleteDependency();
  const updateDependencyMutation = useUpdateDependency();

  // Fetch phases for the current project
  const projectPhasesQuery = useProjectPhases(project?.id);
  const fetchedPhases = projectPhasesQuery.data || [];
  const isPhasesLoading = projectPhasesQuery.isLoading;
  const isPhasesError = projectPhasesQuery.isError;

  const phases = fetchedPhases.length > 0 ? fetchedPhases : (project?.phases || []);

  const {
    deliverables: allDeliverables,
    isLoading: isDeliverablesLoading
  } = useProjectDeliverables(project);

  const { dependencies: projectDependencies } = useProjectDependencies(project?.id);

  const { 
    data: fetchedReleases = [], 
    isLoading: isReleasesLoading, 
    refetch: refetchReleases 
  } = useProjectReleases(project?.id);

  const { data: taskCompletion } = useGetIntelligenceProjectProjectIdTaskCompletion(
    project?.id ?? 0,
    { query: { enabled: !!project?.id } }
  );
  const completionRates = useMemo(() => {
    const phase = new Map<number, number>();
    const release = new Map<number, number>();
    const sprint = new Map<number, number>();
    taskCompletion?.phases?.forEach(c => { if (c.scopeId != null && c.rate != null) phase.set(c.scopeId, c.rate); });
    taskCompletion?.releases?.forEach(c => { if (c.scopeId != null && c.rate != null) release.set(c.scopeId, c.rate); });
    taskCompletion?.sprints?.forEach(c => { if (c.scopeId != null && c.rate != null) sprint.set(c.scopeId, c.rate); });
    return { phase, release, sprint };
  }, [taskCompletion]);

  const [allSprints, setAllSprints] = useState<SprintDto[]>([]);
  const [isSprintsLoading, setIsSprintsLoading] = useState<boolean>(true);

  const [allMilestones, setAllMilestones] = useState<MilestoneDto[]>([]);
  const [isMilestonesLoading, setIsMilestonesLoading] = useState<boolean>(true);

  // Function to fetch all sprints and filter by phase
  const fetchSprintsForPhases = useCallback(async () => {
    if (project?.method === 'CLASSIC') {
      setAllSprints([]);
      setIsSprintsLoading(false);
      return;
    }
    
    if (!phases || phases.length === 0) {
      setAllSprints([]);
      setIsSprintsLoading(false);
      return;
    }
    
    setIsSprintsLoading(true);
    
    try {
      // Get all sprints
      const allSprintsData = await getSprint();
      
      if (allSprintsData && allSprintsData.length > 0) {
        const phaseIds = phases.map(phase => phase.id).filter(Boolean);
        
        const filteredSprints = allSprintsData.filter(sprint => 
          sprint.phase && phaseIds.includes(sprint.phase)
        );
        
        setAllSprints(filteredSprints);
      } else {
        setAllSprints([]);
      }
    } catch (error) {
      console.error("Error loading sprints:", error);
      setAllSprints([]);
    } finally {
      setIsSprintsLoading(false);
    }
  }, [phases, project?.method]);
  
  // trigger refetch sprint data when phases change
  useEffect(() => {
    fetchSprintsForPhases();
  }, [fetchSprintsForPhases, phases.length]);
  
  // get milestones (not phase dependent.)
  const fetchMilestones = useCallback(async () => {
    if (!phases || phases.length === 0) {
      setAllMilestones([]);
      setIsMilestonesLoading(false);
      return;
    }
    
    setIsMilestonesLoading(true);
    
    try {
      let allProjectMilestones: MilestoneDto[] = [];
      
      // Get milestones for each phase
      for (const phase of phases) {
        if (phase.id) {
          const phaseMilestones = await getMilestoneIdPhase(phase.id);
          if (phaseMilestones && phaseMilestones.length > 0) {
            allProjectMilestones = [...allProjectMilestones, ...phaseMilestones];
          }
        }
      }
      
      setAllMilestones(allProjectMilestones);
    } catch (error) {
      console.error("Error loading milestones:", error);
      setAllMilestones([]);
    } finally {
      setIsMilestonesLoading(false);
    }
  }, [phases]);
  
  // Fetch milestones when phases change
  useEffect(() => {
    fetchMilestones();
  }, [fetchMilestones, phases.length]);

  const shouldIncludeDeliverable = (d: DeliverObjectDto): boolean => {
    if (showAgileArtefacts) return true;
      return !isAgileArtefactDeliverable(d);
  };

  // Calculate optimal column width based on project duration and view mode
  const calculateOptimalColumnWidth = useCallback(() => {
    // Get project date range
    let projectStart: Date | null = null;
    let projectEnd: Date | null = null;
    
    // Collect all dates
    const allDates: Date[] = [];
    
    phases.forEach(phase => {
      if (phase.startTime) allDates.push(new Date(phase.startTime));
      if (phase.endTime) allDates.push(new Date(phase.endTime));
    });
    
    allSprints.forEach(sprint => {
      if (sprint.startDate) allDates.push(new Date(sprint.startDate));
      if (sprint.endDate) allDates.push(new Date(sprint.endDate));
    });
    
    fetchedReleases.forEach(release => {
      if (release.startDate) allDates.push(new Date(release.startDate));
      if (release.endDate) allDates.push(new Date(release.endDate));
      else if (release.releaseDate) allDates.push(new Date(release.releaseDate));
    });
    
    allMilestones.forEach(milestone => {
      if (milestone.deadline) {
        const deadlineDate = new Date(milestone.deadline.toString());
        if (!isNaN(deadlineDate.getTime())) {
          allDates.push(deadlineDate);
        }
      }
    });

    if (project?.startTime) allDates.push(new Date(project.startTime));
    if (project?.endTime) allDates.push(new Date(project.endTime));
    
    if (allDates.length > 0) {
      projectStart = new Date(Math.min(...allDates.map(d => d.getTime())));
      projectEnd = new Date(Math.max(...allDates.map(d => d.getTime())));
    }

    if (!projectStart || !projectEnd) {
      return 150;
    }
    
    // Calculate duration
    const durationInDays = Math.ceil((projectEnd.getTime() - projectStart.getTime()) / (1000 * 60 * 60 * 24));
    const durationInMonths = durationInDays / 30;
    const durationInWeeks = durationInDays / 7;

    const availableWidth = window.innerWidth - 280;
    let optimalWidth = 150;
    
    switch (viewMode) {
      case ViewMode.Day:
        const targetDaysOnScreen = Math.min(60, Math.max(30, durationInDays / 2));
        optimalWidth = Math.floor(availableWidth / targetDaysOnScreen);
        optimalWidth = Math.max(30, Math.min(200, optimalWidth));
        break;
        
      case ViewMode.Week:
        const targetWeeksOnScreen = Math.min(16, Math.max(8, durationInWeeks / 2));
        optimalWidth = Math.floor(availableWidth / targetWeeksOnScreen);
        optimalWidth = Math.max(40, Math.min(250, optimalWidth));
        break;
        
      case ViewMode.Month:
        if (durationInMonths < 3) {
          optimalWidth = Math.floor(availableWidth / Math.min(6, durationInMonths + 2));
          optimalWidth = Math.max(150, Math.min(300, optimalWidth));
        } else if (durationInMonths < 12) {
          const targetMonths = Math.min(12, durationInMonths + 2);
          optimalWidth = Math.floor(availableWidth / targetMonths);
          optimalWidth = Math.max(80, Math.min(200, optimalWidth));
        } else {
          const targetMonths = Math.min(24, durationInMonths + 4);
          optimalWidth = Math.floor(availableWidth / targetMonths);
          optimalWidth = Math.max(50, Math.min(150, optimalWidth));
        }
        break;

      case ViewMode.Year:
        const durationInYears = durationInMonths / 12;
        
        if (durationInYears < 2) {
          optimalWidth = Math.floor(availableWidth / Math.min(3, Math.ceil(durationInYears) + 1));
          optimalWidth = Math.max(300, Math.min(600, optimalWidth));
        } else if (durationInYears < 5) {
          const targetYears = Math.min(5, Math.ceil(durationInYears) + 1);
          optimalWidth = Math.floor(availableWidth / targetYears);
          optimalWidth = Math.max(200, Math.min(400, optimalWidth));
        } else {
          const targetYears = Math.min(10, Math.ceil(durationInYears) + 2);
          optimalWidth = Math.floor(availableWidth / targetYears);
          optimalWidth = Math.max(120, Math.min(300, optimalWidth));
        }
        break;
    }
    
    return optimalWidth;
  }, [phases, allSprints, fetchedReleases, allMilestones, project, viewMode]);

  // Update column
  useEffect(() => {
    const newWidth = calculateOptimalColumnWidth();
    setColumnWidth(newWidth);
  }, [calculateOptimalColumnWidth]);

  // Refetch all data when phases etc updated
  useEffect(() => {
    if (!projectPhasesQuery.isLoading && !projectPhasesQuery.isError && projectPhasesQuery.dataUpdatedAt) {
      // Refetch related data when data gets updated from other places.
      fetchSprintsForPhases();
      refetchReleases();
      fetchMilestones();
    }
  }, [projectPhasesQuery.dataUpdatedAt, fetchSprintsForPhases, fetchMilestones, refetchReleases]);

  // Convert project data into gantt chart tasks
  useEffect(() => {
    const hasAny =
      (phases && phases.length > 0) ||
      (fetchedReleases && fetchedReleases.length > 0) ||
      (allSprints && allSprints.length > 0) ||
      (allMilestones && allMilestones.length > 0);
    if (!hasAny) return;

    // Compute timeline range
    const allDates: Date[] = [];
    phases.forEach(p => {
      if (p.startTime) allDates.push(new Date(p.startTime));
      if (p.endTime) allDates.push(new Date(p.endTime));
    });
    allSprints.forEach(s => {
      if (s.startDate) allDates.push(new Date(s.startDate));
      if (s.endDate) allDates.push(new Date(s.endDate));
    });
    fetchedReleases.forEach(r => {
      if (r.startDate) allDates.push(new Date(r.startDate));
      if (r.endDate) allDates.push(new Date(r.endDate));
      else if (r.releaseDate) allDates.push(new Date(r.releaseDate));
    });
    allMilestones.forEach(m => {
      if (m.deadline) {
        const d = new Date(m.deadline.toString());
        if (!isNaN(d.getTime())) allDates.push(d);
      }
    });

    let earliestDate = new Date();
    let latestDate = new Date();
    if (allDates.length > 0) {
      earliestDate = new Date(Math.min(...allDates.map(d => d.getTime())));
      latestDate = new Date(Math.max(...allDates.map(d => d.getTime())));
      earliestDate.setDate(earliestDate.getDate() - 5);
      latestDate.setDate(latestDate.getDate() + 5);
    }

    // Helper
    const sectionRow = (id: string, name: string, displayOrder: number): Task => ({
      id,
      name,
      type: 'task',
      start: earliestDate,
      end: latestDate,
      progress: 0,
      isDisabled: true,
      displayOrder,
      styles: {
        backgroundColor: 'transparent',
        backgroundSelectedColor: 'transparent',
        progressColor: 'transparent',
        progressSelectedColor: 'transparent',
      },
    });

    // Helper
    const buildDeliverable = (
      d: DeliverObjectDto,
      parentId: string,
      parentKind: 'phase' | 'sprint' | 'release',
      displayOrder: number
    ): Task | null => {
      if (!d.startDate || !d.endDate) return null;
      return {
        id: `deliverable-${d.id}-of-${parentKind}-${parentId.split('-')[1]}`,
        name: d.name ? d.name.replaceAll('_', ' ') : 'Deliverable',
        type: 'task',
        start: new Date(d.startDate.toString()),
        end: new Date(d.endDate.toString()),
        progress: d.progress || 0,
        project: parentId,
        displayOrder,
        styles: {
          progressColor: '#0891b2',
          progressSelectedColor: '#0e7490',
          backgroundColor: '#0891b2',
          backgroundSelectedColor: '#0e7490',
        },
      };
    };

    const newTasks: Task[] = [];

    if (phases.length > 0) {
      newTasks.push(sectionRow('phases-group', 'Phases', 1));
      [...phases]
        .sort((a, b) => {
          const aTime = a.startTime ? new Date(a.startTime).getTime() : 0;
          const bTime = b.startTime ? new Date(b.startTime).getTime() : 0;
          return aTime - bTime;
        })
        .forEach(phase => {
        if (!phase.startTime || !phase.endTime || phase.id == null) return;
        const phaseId = `phase-${phase.id}`;
        const phaseDeliverables = allDeliverables.filter(
          (d: DeliverObjectDto) =>
            d.phaseId === phase.id && shouldIncludeDeliverable(d) && d.startDate && d.endDate
        );
        newTasks.push({
          id: phaseId,
            name: getPhaseDisplayName(phase),
          type: 'task',
          start: new Date(phase.startTime),
          end: new Date(phase.endTime),
          progress: completionRates.phase.get(phase.id) ?? 0,
          displayOrder: 1,
          styles: {
            progressColor: '#2563eb',
            progressSelectedColor: '#1d4ed8',
            backgroundColor: '#93c5fd',
            backgroundSelectedColor: '#93c5fd',
          },
        });
        if (expandedRowIds.has(phaseId)) {
          [...phaseDeliverables]
            .sort((a, b) => new Date(a.startDate!.toString()).getTime() - new Date(b.startDate!.toString()).getTime())
            .forEach(d => {
              const t = buildDeliverable(d, phaseId, 'phase', 1);
              if (t) newTasks.push(t);
            });
        }
      });
    }

    if (fetchedReleases && fetchedReleases.length > 0) {
      newTasks.push(sectionRow('releases-group', 'Releases', 2));
      [...fetchedReleases]
        .sort((a, b) => {
          const aTime = a.startDate ? new Date(a.startDate.toString()).getTime()
            : a.releaseDate ? new Date(a.releaseDate.toString()).getTime() : 0;
          const bTime = b.startDate ? new Date(b.startDate.toString()).getTime()
            : b.releaseDate ? new Date(b.releaseDate.toString()).getTime() : 0;
          return aTime - bTime;
        })
        .forEach(release => {
        if (release.id == null) return;
        const startDate = release.startDate ? new Date(release.startDate.toString()) : null;
        let endDate = release.endDate ? new Date(release.endDate.toString()) : null;
        if (!startDate) return;
        if (!endDate && release.releaseDate) endDate = new Date(release.releaseDate.toString());
        if (!endDate) {
          endDate = new Date(startDate);
          endDate.setDate(endDate.getDate() + 1);
        }
        const releaseId = `release-${release.id}`;
        const releaseDeliverables = allDeliverables.filter(
          (d: DeliverObjectDto) => d.releaseId === release.id && shouldIncludeDeliverable(d) && d.startDate && d.endDate
        );
        newTasks.push({
          id: releaseId,
          name: release.name || `Release ${release.id}`,
          type: 'task',
          start: startDate,
          end: endDate,
          progress: completionRates.release.get(release.id) ?? 0,
          displayOrder: 2,
          styles: {
            progressColor: '#059669',
            progressSelectedColor: '#047857',
            backgroundColor: '#6ee7b7',
            backgroundSelectedColor: '#6ee7b7',
          },
        });
        if (expandedRowIds.has(releaseId)) {
          [...releaseDeliverables]
            .sort((a, b) => new Date(a.startDate!.toString()).getTime() - new Date(b.startDate!.toString()).getTime())
            .forEach(d => {
              const t = buildDeliverable(d, releaseId, 'release', 2);
              if (t) newTasks.push(t);
            });
        }
      });
    }

    if (allMilestones && allMilestones.length > 0) {
      newTasks.push(sectionRow('milestones-group', 'Milestones', 3));
      [...allMilestones]
        .sort((a, b) => new Date(a.deadline!.toString()).getTime() - new Date(b.deadline!.toString()).getTime())
        .forEach(milestone => {
        if (!milestone.deadline || milestone.id == null) return;
        const deadlineDate = new Date(milestone.deadline.toString());
        if (isNaN(deadlineDate.getTime())) return;
        newTasks.push({
          id: `milestone-${milestone.id}`,
          name: milestone.name || `Milestone ${milestone.id}`,
          type: 'milestone',
          start: deadlineDate,
          end: deadlineDate,
          progress: 0,
          displayOrder: 3,
          styles: {
            progressColor: '#f59e0b',
            progressSelectedColor: '#d97706',
            backgroundColor: '#f59e0b',
            backgroundSelectedColor: '#d97706',
          },
        });
      });
    }

    if (allSprints && allSprints.length > 0) {
      newTasks.push(sectionRow('sprints-group', 'Sprints', 4));
      [...allSprints]
        .sort((a, b) => {
          const aTime = a.startDate ? new Date(a.startDate).getTime() : 0;
          const bTime = b.startDate ? new Date(b.startDate).getTime() : 0;
          return aTime - bTime;
        })
        .forEach(sprint => {
        if (!sprint.startDate || !sprint.endDate || sprint.id == null) return;
        const sprintId = `sprint-${sprint.id}`;
        const sprintDeliverables = allDeliverables.filter(
          (d: DeliverObjectDto) => d.sprint === sprint.id && shouldIncludeDeliverable(d) && d.startDate && d.endDate
        );
        newTasks.push({
          id: sprintId,
          name: sprint.name || `Sprint ${sprint.id}`,
          type: 'task',
          start: new Date(sprint.startDate),
          end: new Date(sprint.endDate),
          progress: completionRates.sprint.get(sprint.id) ?? 0,
          displayOrder: 4,
          styles: {
            progressColor: '#7c3aed',
            progressSelectedColor: '#6d28d9',
            backgroundColor: '#c4b5fd',
            backgroundSelectedColor: '#c4b5fd',
          },
        });
        if (expandedRowIds.has(sprintId)) {
          [...sprintDeliverables]
            .sort((a, b) => new Date(a.startDate!.toString()).getTime() - new Date(b.startDate!.toString()).getTime())
            .forEach(d => {
              const t = buildDeliverable(d, sprintId, 'sprint', 4);
              if (t) newTasks.push(t);
            });
        }
      });
    }

    if (projectDependencies.length > 0) {
      const deliverableTaskById = new Map<number, Task>();
      newTasks.forEach(t => {
        const match = /^deliverable-(\d+)-of-/.exec(t.id);
        if (match) {
          deliverableTaskById.set(Number(match[1]), t);
        }
      });
      projectDependencies.forEach(dep => {
        if (dep.predecessorId == null || dep.successorId == null) return;
        const predTask = deliverableTaskById.get(dep.predecessorId);
        const succTask = deliverableTaskById.get(dep.successorId);
        if (!predTask || !succTask) return; // only when both endpoints are visible
        const existing = succTask.dependencies ?? [];
        if (!existing.includes(predTask.id)) {
          succTask.dependencies = [...existing, predTask.id];
        }
      });
    }

    setTasks(newTasks);
  }, [phases, fetchedReleases, allSprints, allMilestones, allDeliverables, expandedRowIds, projectDependencies, showAgileArtefacts, completionRates]);

  // Highlight the chosen predecessor bar while connecting two deliverables.
  const displayTasks = useMemo(() => {
    if (!linkMode || linkSourceId == null) return tasks;
    return tasks.map(t => {
      const match = /^deliverable-(\d+)-of-/.exec(t.id.toString());
      if (match && Number(match[1]) === linkSourceId) {
        return {
          ...t,
          styles: {
            ...t.styles,
            backgroundColor: '#f59e0b',
            backgroundSelectedColor: '#f59e0b',
            progressColor: '#d97706',
            progressSelectedColor: '#d97706',
          },
        };
      }
      return t;
    });
  }, [tasks, linkMode, linkSourceId]);

  // Update current date when project changes
  useEffect(() => {
    if (project && project.startTime) {
      const projectStartDate = new Date(project.startTime);
      // date check
      if (!isNaN(projectStartDate.getTime())) { 
        setCurrentDate(projectStartDate);
      }
    }
  }, [project]);

  useEffect(() => {
    if (viewMode === ViewMode.Day) {
      const updateDateHeaders = () => {
        const allElements = document.querySelectorAll('*');
        
        allElements.forEach((element) => {
          const textContent = element.textContent || '';
          
          if (textContent.match(/^(mon|tue|wed|thu|fri|sat|sun),?\s*\d{1,2}$/i) || 
              textContent.match(/^\d{1,2}\s+(mon|tue|wed|thu|fri|sat|sun)$/i) ||
              textContent.match(/^(mon|tue|wed|thu|fri|sat|sun)\s*\d{1,2}$/i)) {
            
            const dayMatch = textContent.match(/\b(\d{1,2})\b/);
            if (dayMatch && dayMatch[1]) {
              element.textContent = dayMatch[1];
              
              const htmlElement = element as HTMLElement;
              htmlElement.style.textAlign = 'center';
              htmlElement.style.fontWeight = '600';
              htmlElement.style.fontSize = '14px';
            }
          }
        });
      };

      const timers = [
        setTimeout(updateDateHeaders, 100),
        setTimeout(updateDateHeaders, 500),
        setTimeout(updateDateHeaders, 1000)
      ];

      const observer = new MutationObserver(() => {
        setTimeout(updateDateHeaders, 50);
      });

      const ganttContainer = document.querySelector('[class*="gantt"]');
      if (ganttContainer) {
        observer.observe(ganttContainer, {
          childList: true,
          subtree: true,
          characterData: true
        });
      }

      return () => {
        timers.forEach(timer => clearTimeout(timer));
        observer.disconnect();
      };
    }
  }, [viewMode, tasks]);

  useEffect(() => {
    const PHASE_BG = '#93c5fd';
    const PHASE_PROGRESS = '#2563eb';

    const fixPhaseBarColors = () => {
      const ganttEl = document.querySelector(`.${styles.ganttContainer}`);
      if (!ganttEl) return;
      // Match phase-X and phase-X-rowY IDs
      const phaseGroups = ganttEl.querySelectorAll('g[id^="phase-"]');
      phaseGroups.forEach((group) => {
        group.querySelectorAll('rect, path, polygon').forEach((el) => {
          if (el instanceof SVGElement) {
            // leave the progress overlay
            if (el.getAttribute('fill') === PHASE_PROGRESS) return;
            el.setAttribute('fill', PHASE_BG);
            (el as SVGElement & { style: CSSStyleDeclaration }).style.fill = PHASE_BG;
            (el as SVGElement & { style: CSSStyleDeclaration }).style.opacity = '1';
          }
        });
      });
    };

    const timers = [
      setTimeout(fixPhaseBarColors, 100),
      setTimeout(fixPhaseBarColors, 500),
      setTimeout(fixPhaseBarColors, 1500),
    ];

    const observer = new MutationObserver(() => {
      setTimeout(fixPhaseBarColors, 50);
    });

    const ganttEl = document.querySelector(`.${styles.ganttContainer}`);
    if (ganttEl) {
      observer.observe(ganttEl, {childList: true, subtree: true});
    }

    return () => {
      timers.forEach((t) => clearTimeout(t));
      observer.disconnect();
    };
  }, [tasks, viewMode]);

  // Month name converting/shorten stuff

  useEffect(() => {
    if (viewMode === ViewMode.Month) {
      const monthMap: Record<string, string> = {
        'January': 'Jan', 'February': 'Feb', 'March': 'Mar',
        'April': 'Apr', 'May': 'May', 'June': 'Jun',
        'July': 'Jul', 'August': 'Aug', 'September': 'Sep',
        'October': 'Oct', 'November': 'Nov', 'December': 'Dec',
      };
      const fullMonthNames = Object.keys(monthMap);

      const shortenMonthNames = () => {
        const ganttEl = document.querySelector(`.${styles.ganttContainer}`);
        if (!ganttEl) return;
        const textEls = ganttEl.querySelectorAll('text');
        textEls.forEach((el) => {
          const txt = el.textContent?.trim();
          if (txt && fullMonthNames.includes(txt)) {
            el.textContent = monthMap[txt];
          }
        });
      };

      const timers = [
        setTimeout(shortenMonthNames, 100),
        setTimeout(shortenMonthNames, 500),
      ];

      const observer = new MutationObserver(() => {
        setTimeout(shortenMonthNames, 50);
      });

      const ganttEl = document.querySelector(`.${styles.ganttContainer}`);
      if (ganttEl) {
        observer.observe(ganttEl, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      }

      return () => {
        timers.forEach(timer => clearTimeout(timer));
        observer.disconnect();
      };
    }
  }, [viewMode, tasks]);

 // Handle responsive height and width
  useEffect(() => {
    const handleResize = () => {
      const newHeight = Math.max(300, window.innerHeight * 0.65);
      setChartHeight(newHeight);
      
      // Recalculate column
      const newWidth = calculateOptimalColumnWidth();
      setColumnWidth(newWidth);
    };
    
    // Set initial height and width
    handleResize();
    window.addEventListener('resize', handleResize);
    
    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [calculateOptimalColumnWidth]);



  const formatLocalDate = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  // Format date for human-readable display (e.g., "15 May 2025")
  const formatHumanReadableDate = (date: Date): string => {
    const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', year: 'numeric' };
    return date.toLocaleDateString(i18n.language === 'de' ? 'de-DE' : 'en-GB', options);
  };

  const handleDateChange = useCallback((task: Task) => {
    // Mark as drag so the onClick handler won't open the detail popup
    isDraggingRef.current = true;

    // Strip stacking suffix e.g. "phase-5-row1" → "phase-5"
    const baseId = task.id.toString().replace(/-row\d+$/, '');
    const newStart = formatLocalDate(task.start);
    const newEnd = formatLocalDate(task.end);

    // Find the original task to get old dates
    const originalTask = tasks.find(t => t.id === task.id);
    const oldStart = originalTask ? formatLocalDate(originalTask.start) : newStart;
    const oldEnd = originalTask ? formatLocalDate(originalTask.end) : newEnd;

    // Optimistically update local task state
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, start: task.start, end: task.end } : t));

    // Determine item type and name
    let itemType: 'phase' | 'sprint' | 'release' | 'deliverable' | 'milestone' = 'phase';
    let itemName = 'Item';
    let isChangeable = true;

    if (baseId.startsWith('phase-')) {
      const phaseId = parseInt(baseId.replace('phase-', ''));
      const phase = phases.find(p => p.id === phaseId);
      itemType = 'phase';
      itemName = phase?.customName || 'Phase';
    } else if (baseId.startsWith('sprint-')) {
      const sprintId = parseInt(baseId.replace('sprint-', ''));
      const sprint = allSprints.find(s => s.id === sprintId);
      itemType = 'sprint';
      itemName = sprint?.name || 'Sprint';
    } else if (baseId.startsWith('release-')) {
      const releaseId = parseInt(baseId.replace('release-', ''));
      const release = fetchedReleases.find(r => r.id === releaseId);
      itemType = 'release';
      itemName = release?.name || 'Release';
    } else if (baseId.startsWith('milestone-')) {
      itemType = 'milestone';
      itemName = 'Milestone';
      isChangeable = false;
    } else if (baseId.startsWith('deliverable-')) {
      const deliverableId = parseInt(baseId.replace('deliverable-', '').split('-of-')[0]);
      const deliverable = allDeliverables.find((d: DeliverObjectDto) => d.id === deliverableId);
      itemType = 'deliverable';
      itemName = deliverable?.name || 'Deliverable';
    }

    // Skip confirmation dialog if not changable things
    if (!isChangeable || (oldStart === newStart && oldEnd === newEnd)) {
      applyDateChange(task, baseId, newStart, newEnd);
      return;
    }

    // Show confirmation dialog for changeable types
    setPendingDateChange({
      task,
      originalTask: originalTask || task,
      oldStartDate: oldStart,
      oldEndDate: oldEnd,
      newStartDate: newStart,
      newEndDate: newEnd,
      oldStartDateDisplay: formatHumanReadableDate(originalTask ? originalTask.start : task.start),
      oldEndDateDisplay: formatHumanReadableDate(originalTask ? originalTask.end : task.end),
      newStartDateDisplay: formatHumanReadableDate(task.start),
      newEndDateDisplay: formatHumanReadableDate(task.end),
      itemType,
      itemName
    });
    setShowDateChangeDialog(true);
  }, [tasks, phases, allSprints, fetchedReleases, allMilestones, allDeliverables]);

  const applyDateChange = async (task: Task, baseId: string, newStart: string, newEnd: string) => {
    try {
      if (baseId.startsWith('phase-')) {
        const phaseId = parseInt(baseId.replace('phase-', ''));
        const phase = phases.find(p => p.id === phaseId);
        if (phase) {
          await putPhaseId(phaseId, { ...phase, startTime: newStart as any, endTime: newEnd as any });
          projectPhasesQuery.refetch();
        }
      } else if (baseId.startsWith('sprint-')) {
        const sprintId = parseInt(baseId.replace('sprint-', ''));
        const sprint = allSprints.find(s => s.id === sprintId);
        if (sprint) {
          await putSprintId(sprintId, { ...sprint, startDate: newStart as any, endDate: newEnd as any });
          fetchSprintsForPhases();
        }
      } else if (baseId.startsWith('release-')) {
        const releaseId = parseInt(baseId.replace('release-', ''));
        const release = fetchedReleases.find(r => r.id === releaseId);
        if (release) {
          await putReleaseId(releaseId, { ...release, startDate: newStart as any, endDate: newEnd as any });
          refetchReleases();
        }
      } else if (baseId.startsWith('milestone-')) {
        const milestoneId = parseInt(baseId.replace('milestone-', ''));
        const milestone = allMilestones.find(m => m.id === milestoneId);
        if (milestone) {
          await putMilestoneId(milestoneId, { ...milestone, deadline: newStart as any });
          fetchMilestones();
        }
      } else if (baseId.startsWith('deliverable-')) {
        const deliverableId = parseInt(baseId.replace('deliverable-', '').split('-of-')[0]);
        const deliverable = allDeliverables.find((d: DeliverObjectDto) => d.id === deliverableId);
        if (deliverable) {
          await putDeliverObjectId(deliverableId, { ...deliverable, startDate: newStart as any, endDate: newEnd as any });
          queryClient.invalidateQueries({ queryKey: ['/deliver-object'] });
        }
      }
      toast.showSuccess('Updated', 'Dates updated successfully');
    } catch {
      toast.showError('Error', 'Failed to update dates');
      // Revert by refetching original data
      projectPhasesQuery.refetch();
    }
  };

  // Handle confirming the date change
  const handleConfirmDateChange = () => {
    if (pendingDateChange) {
      applyDateChange(
        pendingDateChange.task,
        pendingDateChange.task.id.toString().replace(/-row\d+$/, ''),
        pendingDateChange.newStartDate,
        pendingDateChange.newEndDate
      );
    }
    setShowDateChangeDialog(false);
    setPendingDateChange(null);
  };

  // Handle canceling the date change
  const handleCancelDateChange = () => {
    if (pendingDateChange && pendingDateChange.originalTask) {
      // revert cancelled state
      setTasks(prev => prev.map(t => t.id === pendingDateChange.originalTask.id ? pendingDateChange.originalTask : t));
      
      const baseId = pendingDateChange.task.id.toString().replace(/-row\d+$/, '');
      if (baseId.startsWith('phase-')) {
        projectPhasesQuery.refetch();
      } else if (baseId.startsWith('sprint-')) {
        fetchSprintsForPhases();
      } else if (baseId.startsWith('release-')) {
        refetchReleases();
      } else if (baseId.startsWith('milestone-')) {
        fetchMilestones();
      }
    }
    setShowDateChangeDialog(false);
    setPendingDateChange(null);
  };

  // Handle view mode change
  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
  };
  
  // Get project timeframe
  const getProjectTimeframe = () => {
    if (!project?.startTime || !project?.endTime) {
      return 'No project timeline set';
    }
    
    const startDate = new Date(project.startTime);
    const endDate = new Date(project.endTime);
    
    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    };
    
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  // Open the Add New Item popup
  const openAddNewItemPopup = () => {
    setIsAddNewItemPopupOpen(true);
  };
  
  // Close the Add New Item popup
  const closeAddNewItemPopup = () => {
    setIsAddNewItemPopupOpen(false);
  };
  
  // Handle phase creation
  const handlePhaseCreated = (phaseData: PhaseDto) => {
    projectPhasesQuery.refetch();
    if (onProjectUpdate) {
      onProjectUpdate();
    }
  };
  
  // Handle milestone creation
  const handleMilestoneCreated = () => {
    fetchMilestones();
  };
  
  // Handle release creation
  const handleReleaseCreated = () => {
    refetchReleases();
  };
  
  // Handle sprint creation
  const handleSprintCreated = () => {
    fetchSprintsForPhases();
  };

  // Handle task
  const getDeliverableIdFromTask = (task: Task): number | null => {
    const base = task.id.toString().split('-row')[0];
    if (!base.startsWith('deliverable-')) return null;
    const idStr = base.replace('deliverable-', '').split('-of-')[0];
    const id = Number(idStr);
    return Number.isNaN(id) ? null : id;
  };

  // Extract the numeric deliverable id
  const extractDeliverableId = (taskId: string): number | null => {
    const m = /^deliverable-(\d+)-of-/.exec(taskId);
    return m ? Number(m[1]) : null;
  };

  const startLinkMode = () => {
    setLinkSourceId(null);
    setLinkMode(true);
  };

  const cancelLinkMode = () => {
    setLinkMode(false);
    setLinkSourceId(null);
  };

  const openDependencyModal = (defaults?: { predecessorId?: number; successorId?: number }) => {
    setDependencyDefaults(defaults ?? {});
    setIsDependencyModalOpen(true);
  };

  const handleDeleteDependency = async () => {
    if (!clickedDependency?.id) return;
    try {
      await deleteDependencyMutation.mutateAsync({ id: clickedDependency.id });
      toast.showSuccess(t('dependency.toast.deleted'), t('dependency.toast.deletedMsg'));
      setShowDeleteConfirm(false);
      setIsDependencyEditModalOpen(false);
      setClickedDependency(null);
    } catch (error) {
      toast.showError(t('dependency.toast.deleteFailed'), getApiErrorMessage(error));
    }
  };

  const handleUpdateDependency = async () => {
    if (!pendingUpdateData || !clickedDependency?.id) return;
    try {
      await updateDependencyMutation.mutateAsync({ id: clickedDependency.id, data: pendingUpdateData });
      toast.showSuccess(t('dependency.toast.updated'), t('dependency.toast.updatedMsg'));
      setShowUpdateConfirm(false);
      setIsDependencyEditModalOpen(false);
      setClickedDependency(null);
      setPendingUpdateData(null);
    } catch (error) {
      toast.showError(t('dependency.toast.updateFailed'), getApiErrorMessage(error));
    }
  };

  // Handle arrow clicks
  useEffect(() => {
    if (!projectDependencies || projectDependencies.length === 0) return;

    const handleArrowClick = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (!target || typeof target.closest !== 'function') return;

      const arrowGroup = target.closest('g.arrow');
      if (!arrowGroup) return;

      const arrowsContainer = arrowGroup.parentElement;
      if (!arrowsContainer || !arrowsContainer.classList.contains('arrows')) return;

      const allArrowGroups = Array.from(arrowsContainer.children).filter(el =>
        el.classList.contains('arrow')
      );
      const arrowIndex = allArrowGroups.indexOf(arrowGroup);
      if (arrowIndex < 0) return;

      // Reconstruct the exact arrow order
      const orderedPairs: Array<{ predId: number; succId: number }> = [];
      tasks.forEach(predTask => {
        const predId = extractDeliverableId(predTask.id.toString());
        if (predId == null) return;
        tasks.forEach(succTask => {
          if (!succTask.dependencies?.includes(predTask.id)) return;
          const succId = extractDeliverableId(succTask.id.toString());
          if (succId == null) return;
          orderedPairs.push({ predId, succId });
        });
      });
      if (orderedPairs.length !== allArrowGroups.length) return;

      const pair = orderedPairs[arrowIndex];
      if (!pair) return;

      const dep = projectDependencies.find(
        d => d.predecessorId === pair.predId && d.successorId === pair.succId
      );
      if (!dep) return;

      setClickedDependency(dep);
      setIsDependencyEditModalOpen(true);
      e.stopPropagation();
      e.preventDefault();
    };

    window.addEventListener('click', handleArrowClick, true);

    return () => {
      window.removeEventListener('click', handleArrowClick, true);
    };
  }, [projectDependencies, tasks]);

  const handleLinkClick = (task: Task): boolean => {
    if (!linkMode) return false;
    const delId = getDeliverableIdFromTask(task);
    if (delId == null) {
      toast.showWarning(t('dependency.toast.pickDeliverable'), t('dependency.toast.pickDeliverableMsg'));
      return true;
    }
    if (linkSourceId == null) {
      setLinkSourceId(delId);
      return true;
    }
    if (linkSourceId === delId) {
      // Clicking the same bar again clears the selection.
      setLinkSourceId(null);
      return true;
    }
    openDependencyModal({ predecessorId: linkSourceId, successorId: delId });
    setLinkMode(false);
    setLinkSourceId(null);
    return true;
  };

  const handleTaskClick = (task: Task) => {
    if (isDraggingRef.current) {
      isDraggingRef.current = false;
      return;
    }
    if (['phases-group', 'sprints-group', 'releases-group', 'milestones-group'].includes(task.id.toString())) {
      return;
    }
    
    // Extract id
    const taskIdParts = task.id.toString().split('-row');
    const baseTaskId = taskIdParts[0];
    
    setSelectedTask(task);
    
    if (baseTaskId.startsWith('deliverable-')) {
      const deliverableId = baseTaskId.replace('deliverable-', '').split('-of-')[0];
      const deliverable = allDeliverables.find((d: DeliverObjectDto) => d.id?.toString() === deliverableId);
      if (deliverable) {
        setSelectedDeliverable(deliverable);
        setIsDeliverableDetailOpen(true);
      }
      return;
    } else if (baseTaskId.startsWith('phase-')) {
      const phaseId = baseTaskId.replace('phase-', '');
      const phase = phases.find(p => p.id?.toString() === phaseId);
      if (phase) {
        setSelectedTaskData(phase);
        setSelectedTaskType('phase');
        setIsDetailDialogOpen(true);
      }
    } else if (baseTaskId.startsWith('release-')) {
      const releaseId = baseTaskId.replace('release-', '');
      const release = fetchedReleases.find(r => r.id?.toString() === releaseId);
      if (release) {
        setSelectedTaskData(release);
        setSelectedTaskType('release');
        setIsDetailDialogOpen(true);
      }
    } else if (baseTaskId.startsWith('sprint-')) {
      const sprintId = baseTaskId.replace('sprint-', '');
      const sprint = allSprints.find(s => s.id?.toString() === sprintId);
      if (sprint) {
        setSelectedTaskData(sprint);
        setSelectedTaskType('sprint');
        setIsDetailDialogOpen(true);
      }
    } else if (baseTaskId.startsWith('milestone-')) {
      const milestoneId = baseTaskId.replace('milestone-', '');
      const milestone = allMilestones.find(m => m.id?.toString() === milestoneId);
      if (milestone) {
        setSelectedTaskData(milestone);
        setSelectedTaskType('milestone');
        setIsDetailDialogOpen(true);
      }
    }
  };

  return (
    <div className={styles.phasePlanV2}>
      <div className={styles.planHeader}>
        <div className={styles.planHeaderContent}>
          <h2 className={styles.planTitle}>{t('cockpit.title', 'Cockpit')}</h2>
          <p className={styles.planDescription}>{t('cockpit.description', 'Manage project phases, sprints, releases, and milestones in a visual timeline.')}</p>
        </div>
      </div>
      <div className={styles.phasePlanContent}>
        <div className={styles.timelineControls}>
          <div className={styles.navigationButtons}>
            <h3 className={styles.timelineTitle}>
              {getProjectTimeframe()}
            </h3>
          </div>
          
          <div className={styles.rightControls}>
            <div className={styles.filterControls}>
              <label className={styles.filterCheckbox}>
                <input
                  type="checkbox"
                  checked={showAgileArtefacts}
                  onChange={(e) => setShowAgileArtefacts(e.target.checked)}
                />
                <span>{t('dependency.modal.showAgileArtefacts')}</span>
              </label>
            </div>

            <button 
              className={styles.addNewButton}
              onClick={openAddNewItemPopup}
              aria-label={t('cockpit.addNewItem', 'Add new item')}
              disabled={isPhasesLoading}
            >
              <IconTimelineEventPlus />
              <span className={styles.addNewButtonText}>{t('cockpit.addNewItem', 'Add new item')}</span>
            </button>

            <button
              className={styles.addNewButton}
              onClick={() => openDependencyModal()}
              aria-label={t('dependency.ariaAddDep')}
              disabled={isPhasesLoading || allDeliverables.length < 2}
              title={allDeliverables.length < 2 ? t('dependency.buttons.needTwoDels') : t('dependency.buttons.addDepBetween')}
            >
              <IconArrowsSplit2 />
              <span className={styles.addNewButtonText}>{t('dependency.addDependency')}</span>
            </button>

            <button
              className={`${styles.addNewButton} ${linkMode ? styles.addNewButtonActive : ''}`}
              onClick={() => (linkMode ? cancelLinkMode() : startLinkMode())}
              aria-label="Connect deliverables on chart"
              disabled={isPhasesLoading || allDeliverables.length < 2}
              title={allDeliverables.length < 2 ? t('dependency.buttons.needTwoDels') : t('dependency.buttons.connectBars')}
            >
              <IconLink />
              <span className={styles.addNewButtonText}>{linkMode ? t('dependency.cancelLink') : t('dependency.connectOnChart')}</span>
            </button>
            
            <div className={styles.viewModeButtons}>
              <button
                className={`${styles.viewModeButton} ${viewMode === ViewMode.Week ? styles.active : ''}`}
                onClick={() => handleViewModeChange(ViewMode.Week)}
              >
                {t('cockpit.viewWeekly', 'Weekly')}
              </button>
              <button
                className={`${styles.viewModeButton} ${viewMode === ViewMode.Month ? styles.active : ''}`}
                onClick={() => handleViewModeChange(ViewMode.Month)}
              >
                {t('cockpit.viewMonthly', 'Monthly')}
              </button>
              <button
                className={`${styles.viewModeButton} ${viewMode === ViewMode.Year ? styles.active : ''}`}
                onClick={() => handleViewModeChange(ViewMode.Year)}
              >
                {t('cockpit.viewYearly', 'Yearly')}
              </button>
            </div>
          </div>
        </div>
        
        <div className={styles.ganttContainer}>
          {linkMode && (
            <div className={styles.linkModeBanner}>
              <IconLink size={18} />
              <span className={styles.linkModeText}>
                {linkSourceId == null
                  ? t('dependency.linkModeHintPredecessor')
                  : t('dependency.linkModeHintSuccessor')}
              </span>
              {linkSourceId != null && (
                <button className={styles.linkModeReset} onClick={() => setLinkSourceId(null)}>
                  {t('dependency.linkModeReset')}
                </button>
              )}
              <button className={styles.linkModeCancel} onClick={cancelLinkMode}>
                {t('dependency.linkModeCancel')}
              </button>
            </div>
          )}
          {tasks.length > 0 ? (
            <div className={`${styles.customGanttWrapper} ${viewMode === ViewMode.Day ? styles.dailyView : ''} ${linkMode ? styles.linkModeActive : ''}`}>
              <Gantt
                tasks={displayTasks}
                viewMode={viewMode}
                onDateChange={handleDateChange}
                onDoubleClick={() => {}}
                onClick={(task) => {
                  // Bar click in the chart area opens the detail popup
                  if (task.id.toString().endsWith('-group')) return;
                  if (isDraggingRef.current) { isDraggingRef.current = false; return; }
                  if (handleLinkClick(task)) return;
                  handleTaskClick(task);
                }}
                onSelect={() => {}}
                locale='locale'
                onExpanderClick={(task) => {
                  const id = task.id.toString();
                  if (id.startsWith('phase-') || id.startsWith('sprint-') || id.startsWith('release-')) {
                    toggleExpand(id);
                  }
                }}
                columnWidth={columnWidth}
                listCellWidth="220px"
                rowHeight={40}
                ganttHeight={chartHeight}
                todayColor="rgba(45, 120, 219, 0.15)"
                viewDate={currentDate}
                barCornerRadius={4}
                handleWidth={12}
                arrowColor="rgba(45, 120, 219, 0.55)"
                arrowIndent={24}
                fontFamily="'Inter', 'Onest', sans-serif"
                TooltipContent={({ task }) => {
                  const isGroup = task.id.toString().endsWith('-group');
                  if (isGroup) return null;
                  const fmt = (d: Date) => d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                  const isMilestone = task.type === 'milestone';
                  return (
                    <div style={{
                      background: '#1e293b',
                      color: '#f1f5f9',
                      borderRadius: 6,
                      padding: '6px 10px',
                      fontSize: 12,
                      lineHeight: 1.6,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                      maxWidth: 220,
                      pointerEvents: 'none',
                    }}>
                      <div style={{ fontWeight: 600, marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{task.name}</div>
                      {isMilestone
                        ? <div style={{ color: '#94a3b8' }}>{fmt(task.start)}</div>
                        : <div style={{ color: '#94a3b8' }}>{fmt(task.start)} → {fmt(task.end)}</div>
                      }
                    </div>
                  );
                }}
                TaskListHeader={({ headerHeight, fontFamily, fontSize }) => (
                  <div
                    className={styles.customTaskListHeader}
                    style={{
                      height: headerHeight,
                      fontFamily: fontFamily,
                      fontSize: fontSize,
                    }}
                  >
                    <div className={styles.taskListHeaderItem} />
                  </div>
                )}
                TaskListTable={({ rowHeight, fontFamily, fontSize, tasks, selectedTaskId, setSelectedTask, onExpanderClick }) => (
                  <div
                    className={styles.customTaskListTable}
                    style={{
                      fontFamily: fontFamily,
                      fontSize: fontSize,
                    }}
                  >
                    {tasks.map(task => {
                      const tid = task.id.toString();
                      const isSection = tid.endsWith('-group');
                      const isDeliverable = tid.startsWith('deliverable-');
                      const isPhase = tid.startsWith('phase-') && !isSection;
                      const isSprint = tid.startsWith('sprint-') && !isSection;
                      const isRelease = tid.startsWith('release-') && !isSection;
                      const isMilestone = tid.startsWith('milestone-') && !isSection;

                      // Determine if this row can be expanded and current state
                      let hasChildren = false;
                      if (isPhase) {
                        const phaseIdNum = parseInt(tid.replace('phase-', ''));
                        hasChildren = allDeliverables.some((d: DeliverObjectDto) =>
                          d.phaseId === phaseIdNum && shouldIncludeDeliverable(d) && d.startDate && d.endDate
                        );
                      } else if (isSprint) {
                        const sprintIdNum = parseInt(tid.replace('sprint-', ''));
                        hasChildren = allDeliverables.some((d: DeliverObjectDto) =>
                          d.sprint === sprintIdNum && d.startDate && d.endDate
                        );
                      } else if (isRelease) {
                        const releaseIdNum = parseInt(tid.replace('release-', ''));
                        hasChildren = allDeliverables.some((d: DeliverObjectDto) =>
                          d.releaseId === releaseIdNum && d.startDate && d.endDate
                        );
                      }
                      const isExpanded = expandedRowIds.has(tid);

                      // Color dot identifier
                      let dotColor = '';
                      if (isPhase) dotColor = '#2563eb';
                      else if (isSprint) dotColor = '#7c3aed';
                      else if (isRelease) dotColor = '#059669';
                      else if (isMilestone) dotColor = '#f59e0b';
                      else if (isDeliverable) dotColor = '#0891b2';

                      const rowClass = [
                        styles.customTaskListRow,
                        isSection ? styles.sectionRow : '',
                        isDeliverable ? styles.deliverableRow : '',
                        selectedTaskId === task.id ? styles.selectedRow : '',
                      ].filter(Boolean).join(' ');

                      // expandable groups
                      const isExpandable = isPhase || isSprint || isRelease;
                      const handleRowClick = () => {
                        if (isSection) return;
                        if (isExpandable && hasChildren) {
                          toggleExpand(tid);
                          return;
                        }
                        setSelectedTask(task.id);
                        handleTaskClick(task);
                      };

                      return (
                        <div
                          className={rowClass}
                          style={{ height: rowHeight }}
                          key={task.id}
                          onClick={handleRowClick}
                          title={
                            isExpandable && hasChildren
                              ? (isExpanded ? 'Click to collapse' : 'Click to expand connected deliverables')
                              : isExpandable
                                ? 'No connected deliverables'
                                : undefined
                          }
                        >
                          {isSection ? (
                            <div className={styles.sectionLabel}>{task.name}</div>
                          ) : (
                            <div className={styles.customTaskListCell}>
                              {isExpandable ? (
                                <button
                                  type="button"
                                  className={`${styles.expander} ${!hasChildren ? styles.expanderDisabled : ''} ${isExpanded ? styles.expanderOpen : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (hasChildren) toggleExpand(tid);
                                  }}
                                  aria-label={isExpanded ? 'Collapse' : 'Expand'}
                                  disabled={!hasChildren}
                                >
                                  <svg width="12" height="12" viewBox="0 0 12 12" style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}>
                                    <path d="M4 2L8 6L4 10" stroke="currentColor" strokeWidth="1.75" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                </button>
                              ) : (
                                <div className={styles.expanderSpacer} />
                              )}
                              {isDeliverable && <div className={styles.deliverableIndent} />}
                              {dotColor && <span className={styles.colorDot} style={{ background: dotColor }} />}
                              <div className={styles.taskName}>{task.name}</div>
                              {isExpandable && hasChildren && (
                                <span className={styles.childCountBadge}>
                                  {isPhase
                                    ? allDeliverables.filter((d: DeliverObjectDto) => d.phaseId === parseInt(tid.replace('phase-', '')) && shouldIncludeDeliverable(d) && d.startDate && d.endDate).length
                                    : isSprint
                                      ? allDeliverables.filter((d: DeliverObjectDto) => d.sprint === parseInt(tid.replace('sprint-', '')) && shouldIncludeDeliverable(d) && d.startDate && d.endDate).length
                                      : allDeliverables.filter((d: DeliverObjectDto) => d.releaseId === parseInt(tid.replace('release-', '')) && shouldIncludeDeliverable(d) && d.startDate && d.endDate).length}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              />
            </div>
          ) : (
            <div className={styles.noDataMessage}>
              {isPhasesLoading || isSprintsLoading || isReleasesLoading || isMilestonesLoading ? (
                <p>Loading timeline data...</p>
              ) : (
                <p>No timeline data available. Add phases, sprints, releases, or milestones to see them here.</p>
              )}
            </div>
          )}
        </div>
      </div>

      <AddNewItemPopup
        isOpen={isAddNewItemPopupOpen}
        onClose={closeAddNewItemPopup}
        projectId={project?.id}
        projectType={project?.projectType}
        methodType={project?.method}
        phases={phases}
        releases={fetchedReleases}
        onPhaseCreated={handlePhaseCreated}
        onSprintCreated={handleSprintCreated}
        onReleaseCreated={handleReleaseCreated}
        onMilestoneCreated={handleMilestoneCreated}
      />

      <DependencyModal
        isOpen={isDependencyModalOpen}
        onClose={() => setIsDependencyModalOpen(false)}
        deliverables={allDeliverables}
        phases={phases}
        defaultPredecessorId={dependencyDefaults.predecessorId}
        defaultSuccessorId={dependencyDefaults.successorId}
      />

      <DependencyModal
        isOpen={isDependencyEditModalOpen}
        onClose={() => {
          setIsDependencyEditModalOpen(false);
          setClickedDependency(null);
          setPendingUpdateData(null);
        }}
        deliverables={allDeliverables}
        phases={phases}
        existing={clickedDependency}
        onUpdate={(data) => {
          setPendingUpdateData(data);
          setShowUpdateConfirm(true);
        }}
        onDelete={() => setShowDeleteConfirm(true)}
      />

      <ConfirmationDialog
        isOpen={showUpdateConfirm}
        onClose={() => {
          setShowUpdateConfirm(false);
        }}
        onConfirm={handleUpdateDependency}
        title={t('dependency.confirm.updateTitle')}
        message={t('dependency.confirm.updateMessage')}
        confirmText={t('dependency.confirm.updateConfirm')}
        cancelText={t('dependency.confirm.cancel')}
        type="confirm"
      />

      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
        }}
        onConfirm={handleDeleteDependency}
        title={t('dependency.confirm.removeTitle')}
        message={t('dependency.confirm.removeMessage')}
        confirmText={t('dependency.confirm.removeConfirm')}
        cancelText={t('dependency.confirm.cancel')}
        type="danger"
      />

      <GanttItemDetailModal
        isOpen={isDetailDialogOpen}
        onClose={() => {
          setIsDetailDialogOpen(false);
          setSelectedTask(null);
          setSelectedTaskData(null);
          setSelectedTaskType(null);
        }}
        itemType={(selectedTaskType === 'deliverable' ? 'phase' : selectedTaskType) || 'phase'}
        itemData={selectedTaskData}
        availablePhases={phases}
        projectId={project?.id}
        projectType={project?.method}
        onUpdate={() => {
          if (selectedTaskType === 'phase') {
            projectPhasesQuery.refetch();
            if (onProjectUpdate) {
              onProjectUpdate();
            }
          } else if (selectedTaskType === 'sprint') {
            fetchSprintsForPhases();
          } else if (selectedTaskType === 'release') {
            refetchReleases();
          } else if (selectedTaskType === 'milestone') {
            fetchMilestones();
          }
        }}
        onDelete={() => {
          if (selectedTaskType === 'phase') {
            projectPhasesQuery.refetch();
            if (onProjectUpdate) {
              onProjectUpdate();
            }
          } else if (selectedTaskType === 'sprint') {
            fetchSprintsForPhases();
          } else if (selectedTaskType === 'release') {
            refetchReleases();
          } else if (selectedTaskType === 'milestone') {
            fetchMilestones();
          }
        }}
      />

      {selectedDeliverable && (
          <DeliverableDetailPopup
              isOpen={isDeliverableDetailOpen}
              onClose={() => {
                setIsDeliverableDetailOpen(false);
                setSelectedDeliverable(null);
              }}
              deliverable={selectedDeliverable as any}
              rawDeliverables={allDeliverables}
              projectId={project?.id}
              project={project}
              onDeliverableUpdate={() => {
                if (onProjectUpdate) onProjectUpdate();
              }}
          />
      )}

      <ConfirmationDialog
        isOpen={showDateChangeDialog}
        onClose={handleCancelDateChange}
        onConfirm={handleConfirmDateChange}
        title={t('dateChangeConfirmation.title')}
        message={
          pendingDateChange
            ? t('dateChangeConfirmation.messageTemplate', {
                itemName: pendingDateChange.itemName,
                oldStart: pendingDateChange.oldStartDateDisplay,
                oldEnd: pendingDateChange.oldEndDateDisplay,
                newStart: pendingDateChange.newStartDateDisplay,
                newEnd: pendingDateChange.newEndDateDisplay
              })
            : t('dateChangeConfirmation.messageTemplate')
        }
        confirmText={t('dateChangeConfirmation.buttons.confirm')}
        cancelText={t('dateChangeConfirmation.buttons.cancel')}
        type="confirm"
      />
    </div>
  );
};

export default PhasePlan;
